import { useCallback, useState } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { confirmSuppliers, downloadNoSupplier } from './queueActions'
import type { BulkSupplierInput } from '../../shared/api/procurementRequests.api'
import { toast } from '../../shared/ui/toastStore'
import type { QueueRow } from './queueTypes'

/**
 * Логика окна «Кому отправляем запрос?» в очереди квот: какие позиции уходят в
 * SupplierSelectModal (из основного грида или из секций «В работе»/«Обработано»)
 * + подтверждение/скачивание без поставщика. Вынесено из ProcurementQueuePage
 * (файл за лимитом 500 строк).
 *
 * Подтверждение — v2: несколько поставщиков сразу (раскладка «Разложить по
 * поставщикам», BulkSupplierInput с персональным набором позиций) + опциональный
 * прямой запрос производителю. confirmSuppliers возвращает отчёт о частичной
 * сборке BOM производителю (какие позиции не вошли и кто их держит) → toast.
 */
export function useQueueSuppliers(params: {
  selectedRows: QueueRow[]
  queryClient: QueryClient
  invalidateQueue: () => void
  deselectAll: () => void
}) {
  const { selectedRows, queryClient, invalidateQueue, deselectAll } = params
  // Строки, уходящие в SupplierSelectModal.
  const [pendingRows, setPendingRows] = useState<QueueRow[]>([])
  const [supplierModalOpen, setSupplierModalOpen] = useState(false)

  // Кнопка тулбара: выбранные позиции → попап выбора поставщиков.
  const handleDownloadRequest = useCallback(() => {
    if (selectedRows.length === 0) return
    setPendingRows(selectedRows)
    setSupplierModalOpen(true)
  }, [selectedRows])

  // Переотправка из секции: позиции с заготовкой/нулевой квотой снова уходят
  // другому поставщику (запрос руководителя закупок 07.07).
  const handleSectionSendRequest = useCallback((rows: QueueRow[]) => {
    if (rows.length === 0) return
    setPendingRows(rows)
    setSupplierModalOpen(true)
  }, [])

  const handleConfirmSuppliers = useCallback(
    async (suppliers: BulkSupplierInput[], note: string | null, manufacturer: string | null) => {
      const report = await confirmSuppliers({
        selectedRows: pendingRows,
        suppliers,
        note,
        manufacturer,
        queryClient,
        invalidateQueue,
        setSupplierModalOpen,
      })
      // Частичная сборка BOM производителю: какие позиции не вошли и кто их держит.
      if (report) toast.info(report)
      // После переотправки строка может остаться в той же секции — снимаем
      // выделение во всех гридах, чтобы старый выбор не вводил в заблуждение.
      deselectAll()
    },
    [pendingRows, invalidateQueue, queryClient, deselectAll],
  )

  const handleDownloadNoSupplier = useCallback(
    () => downloadNoSupplier({ selectedRows: pendingRows, setSupplierModalOpen }),
    [pendingRows],
  )

  return {
    pendingRows,
    supplierModalOpen,
    setSupplierModalOpen,
    handleDownloadRequest,
    handleSectionSendRequest,
    handleConfirmSuppliers,
    handleDownloadNoSupplier,
  }
}
